import React, { useState, useEffect } from 'react';
import { getServices, getServiceCategories, getDocumentTypes, createService, updateService, getService } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Search, 
  Plus, 
  Save, 
  Edit, 
  MoreHorizontal, 
  FileText, 
  Clock, 
  Building2, 
  Users,
  DollarSign,
  Tag,
  CheckCircle,
  Info,
  ChevronRight,
  Briefcase,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const serviceTypes = ['New', 'Renewal', 'Amendment', 'Cancellation', 'Other'];
const applicableToOptions = ['Company', 'Worker', 'Both'];

export default function ServicesPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'Admin' || user?.role === 'Manager';
  
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  const [formData, setFormData] = useState({
    name: '',
    name_arabic: '',
    category_id: '',
    service_type: 'New',
    applicable_to: 'Both',
    govt_fee: 0,
    govt_fee_variable: false,
    service_fee_default: 0,
    processing_days_min: null,
    processing_days_max: null,
    authority: '',
    description: '',
    notes: '',
    instructions: '',
    required_documents: [],
    is_active: true
  });

  useEffect(() => { loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (search) loadServices();
  }, [search]);

  const loadData = async () => {
    try {
      const [servicesRes, categoriesRes, docTypesRes] = await Promise.all([
        getServices({ limit: 200 }),
        getServiceCategories(),
        getDocumentTypes()
      ]);
      setServices(servicesRes.data.items || []);
      setCategories(categoriesRes.data || []);
      setDocumentTypes(docTypesRes.data || []);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const res = await getServices({ search, limit: 200 });
      setServices(res.data.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewServiceDetail = async (service) => {
    try {
      const res = await getService(service.id);
      setSelectedService(res.data);
      setDetailSheetOpen(true);
    } catch (error) {
      toast.error('Failed to load service details');
    }
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name || '',
        name_arabic: service.name_arabic || '',
        category_id: service.category_id || '',
        service_type: service.service_type || 'New',
        applicable_to: service.applicable_to || 'Both',
        govt_fee: service.govt_fee || 0,
        govt_fee_variable: service.govt_fee_variable || false,
        service_fee_default: service.service_fee_default || 0,
        processing_days_min: service.processing_days_min || null,
        processing_days_max: service.processing_days_max || null,
        authority: service.authority || '',
        description: service.description || '',
        notes: service.notes || '',
        instructions: service.instructions || '',
        required_documents: service.required_documents || [],
        is_active: service.is_active !== false
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        name_arabic: '',
        category_id: categories[0]?.id || '',
        service_type: 'New',
        applicable_to: 'Both',
        govt_fee: 0,
        govt_fee_variable: false,
        service_fee_default: 0,
        processing_days_min: null,
        processing_days_max: null,
        authority: '',
        description: '',
        notes: '',
        instructions: '',
        required_documents: [],
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleToggleRequiredDoc = (docTypeId) => {
    setFormData(prev => {
      const docs = prev.required_documents || [];
      if (docs.includes(docTypeId)) {
        return { ...prev, required_documents: docs.filter(d => d !== docTypeId) };
      } else {
        return { ...prev, required_documents: [...docs, docTypeId] };
      }
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category_id) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingService) {
        await updateService(editingService.id, formData);
        toast.success('Service updated');
      } else {
        await createService(formData);
        toast.success('Service created');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  // Group services by category
  const groupedServices = categories.map((cat) => ({
    ...cat,
    services: services.filter((s) => s.category_id === cat.id)
  }));

  const getCategoryName = (catId) => categories.find(c => c.id === catId)?.name || 'Uncategorized';
  const getCategoryIcon = (catId) => categories.find(c => c.id === catId)?.icon || '📋';

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="skeleton h-8 w-48 rounded-lg" />
          <div className="skeleton h-10 w-32 rounded-lg" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-48 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Services Catalog</h1>
          <p className="text-muted-foreground">UAE Typing Center Services</p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={viewMode} onValueChange={setViewMode} className="hidden sm:block">
            <TabsList>
              <TabsTrigger value="cards">Cards</TabsTrigger>
              <TabsTrigger value="table">Table</TabsTrigger>
            </TabsList>
          </Tabs>
          {canEdit && (
            <Button onClick={() => handleOpenDialog()} data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          )}
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="search-services"
        />
      </div>

      {viewMode === 'cards' ? (
        // Card View
        groupedServices.map((cat) => cat.services.length > 0 && (
          <div key={cat.id} className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{cat.icon}</span>
              <h2 className="text-lg font-semibold">{cat.name}</h2>
              <Badge variant="secondary">{cat.services.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {cat.services.map((svc) => (
                <Card 
                  key={svc.id} 
                  className="modern-card cursor-pointer group"
                  onClick={() => handleViewServiceDetail(svc)}
                  data-testid={`service-card-${svc.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base group-hover:text-teal-600 transition-colors">
                          {svc.name}
                        </CardTitle>
                        <CardDescription className="text-xs font-mono">{svc.service_code}</CardDescription>
                      </div>
                      <Badge variant={svc.is_active !== false ? 'default' : 'secondary'} className="text-xs">
                        {svc.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <DollarSign className="w-3.5 h-3.5" />
                        <span className="font-mono font-semibold text-foreground">
                          {svc.govt_fee_variable ? '~' : ''}{formatCurrency((svc.govt_fee || 0) + (svc.service_fee_default || 0))}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        {svc.applicable_to === 'Worker' ? <Users className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                        <span>{svc.applicable_to}</span>
                      </div>
                    </div>
                    
                    {(svc.required_documents?.length > 0 || svc.processing_days_min) && (
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {svc.required_documents?.length > 0 && (
                          <div className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            <span>{svc.required_documents.length} docs required</span>
                          </div>
                        )}
                        {svc.processing_days_min && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{svc.processing_days_min}-{svc.processing_days_max || svc.processing_days_min} days</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t">
                      <Badge variant="outline" className="text-xs">{svc.service_type || 'New'}</Badge>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-teal-600 transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))
      ) : (
        // Table View
        groupedServices.map((cat) => cat.services.length > 0 && (
          <Card key={cat.id}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <span className="text-2xl">{cat.icon}</span>
                <span>{cat.name}</span>
                <Badge variant="secondary" className="ml-2">{cat.services.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left py-2 px-2">Code</th>
                      <th className="text-left py-2 px-2">Service Name</th>
                      <th className="text-left py-2 px-2">Type</th>
                      <th className="text-right py-2 px-2">Fees</th>
                      <th className="text-center py-2 px-2">Docs</th>
                      <th className="text-left py-2 px-2">For</th>
                      <th className="text-left py-2 px-2">Status</th>
                      {canEdit && <th className="text-right py-2 px-2">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {cat.services.map((svc) => (
                      <tr 
                        key={svc.id} 
                        className="border-b border-border/50 hover:bg-teal-50/30 cursor-pointer" 
                        onClick={() => handleViewServiceDetail(svc)}
                        data-testid={`service-row-${svc.id}`}
                      >
                        <td className="py-2 px-2 font-mono text-xs">{svc.service_code}</td>
                        <td className="py-2 px-2 font-medium">{svc.name}</td>
                        <td className="py-2 px-2"><Badge variant="outline">{svc.service_type || 'New'}</Badge></td>
                        <td className="py-2 px-2 text-right font-mono">
                          {formatCurrency((svc.govt_fee || 0) + (svc.service_fee_default || 0))}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {svc.required_documents?.length > 0 ? (
                            <Badge variant="secondary" className="text-xs">{svc.required_documents.length}</Badge>
                          ) : '-'}
                        </td>
                        <td className="py-2 px-2"><Badge variant="secondary">{svc.applicable_to}</Badge></td>
                        <td className="py-2 px-2">
                          <Badge variant={svc.is_active !== false ? 'default' : 'secondary'}>
                            {svc.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        {canEdit && (
                          <td className="py-2 px-2 text-right" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleOpenDialog(svc)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Service Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedService && (
            <>
              <SheetHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <SheetTitle className="text-xl">{selectedService.name}</SheetTitle>
                    <p className="text-sm font-mono text-muted-foreground">{selectedService.service_code}</p>
                  </div>
                  <Badge variant={selectedService.is_active !== false ? 'default' : 'secondary'}>
                    {selectedService.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </SheetHeader>

              <div className="space-y-6">
                {/* Quick Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Total Fee</p>
                    <p className="text-lg font-bold font-mono">
                      {formatCurrency((selectedService.govt_fee || 0) + (selectedService.service_fee_default || 0))}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Govt: {formatCurrency(selectedService.govt_fee || 0)} + Service: {formatCurrency(selectedService.service_fee_default || 0)}
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Processing Time</p>
                    <p className="text-lg font-bold">
                      {selectedService.processing_days_min ? 
                        `${selectedService.processing_days_min}-${selectedService.processing_days_max || selectedService.processing_days_min} days` : 
                        'Not specified'
                      }
                    </p>
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <Info className="w-4 h-4" /> Service Details
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">Category</p>
                      <p className="font-medium flex items-center gap-1">
                        <span>{getCategoryIcon(selectedService.category_id)}</span>
                        {selectedService.category?.name || getCategoryName(selectedService.category_id)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Type</p>
                      <p className="font-medium">{selectedService.service_type || 'New'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Applicable To</p>
                      <p className="font-medium flex items-center gap-1">
                        {selectedService.applicable_to === 'Worker' ? <Users className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                        {selectedService.applicable_to}
                      </p>
                    </div>
                    {selectedService.authority && (
                      <div>
                        <p className="text-muted-foreground text-xs">Authority</p>
                        <p className="font-medium">{selectedService.authority}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedService.description && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground">{selectedService.description}</p>
                  </div>
                )}

                {/* Required Documents */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Required Documents
                    {selectedService.required_documents?.length > 0 && (
                      <Badge variant="secondary">{selectedService.required_documents.length}</Badge>
                    )}
                  </h4>
                  {selectedService.required_document_details?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedService.required_document_details.map((doc, idx) => (
                        <div key={doc.id || idx} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-sm font-medium">{doc.name}</span>
                          {doc.requires_expiry && (
                            <Badge variant="outline" className="text-xs ml-auto">Has Expiry</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : selectedService.required_documents?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedService.required_documents.map((docId, idx) => {
                        const docType = documentTypes.find(d => d.id === docId);
                        return (
                          <div key={docId} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg">
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                            <span className="text-sm font-medium">{docType?.name || docId}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No specific documents required</p>
                  )}
                </div>

                {selectedService.instructions && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Instructions
                    </h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap bg-amber-50 p-3 rounded-lg border border-amber-200">
                      {selectedService.instructions}
                    </p>
                  </div>
                )}

                {selectedService.notes && (
                  <div>
                    <h4 className="font-semibold text-sm mb-2">Notes</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedService.notes}</p>
                  </div>
                )}

                {/* Actions */}
                {canEdit && (
                  <div className="flex gap-2 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        setDetailSheetOpen(false);
                        handleOpenDialog(selectedService);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-2" /> Edit Service
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add/Edit Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 pr-4">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="pricing">Pricing</TabsTrigger>
                <TabsTrigger value="documents">Required Docs</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Service Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g., Employment Visa New"
                      data-testid="service-name-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Name (Arabic)</Label>
                    <Input
                      value={formData.name_arabic}
                      onChange={(e) => setFormData(p => ({ ...p, name_arabic: e.target.value }))}
                      dir="rtl"
                    />
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <Select value={formData.category_id} onValueChange={(v) => setFormData(p => ({ ...p, category_id: v }))}>
                      <SelectTrigger data-testid="service-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.icon} {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Service Type</Label>
                    <Select value={formData.service_type} onValueChange={(v) => setFormData(p => ({ ...p, service_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Applicable To</Label>
                    <Select value={formData.applicable_to} onValueChange={(v) => setFormData(p => ({ ...p, applicable_to: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {applicableToOptions.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Authority</Label>
                    <Input
                      value={formData.authority}
                      onChange={(e) => setFormData(p => ({ ...p, authority: e.target.value }))}
                      placeholder="e.g., MOHRE, ICP, DED"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={formData.govt_fee_variable}
                        onCheckedChange={(v) => setFormData(p => ({ ...p, govt_fee_variable: v }))}
                      />
                      <Label>Government Fee is Variable</Label>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Enable if government fee varies based on case
                    </p>
                  </div>
                  {!formData.govt_fee_variable && (
                    <div>
                      <Label>Government Fee (AED)</Label>
                      <Input
                        type="number"
                        value={formData.govt_fee}
                        onChange={(e) => setFormData(p => ({ ...p, govt_fee: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                  )}
                  <div>
                    <Label>Service Fee (AED)</Label>
                    <Input
                      type="number"
                      value={formData.service_fee_default}
                      onChange={(e) => setFormData(p => ({ ...p, service_fee_default: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label>Processing Days (Min)</Label>
                    <Input
                      type="number"
                      value={formData.processing_days_min || ''}
                      onChange={(e) => setFormData(p => ({ ...p, processing_days_min: parseInt(e.target.value) || null }))}
                      placeholder="e.g., 3"
                    />
                  </div>
                  <div>
                    <Label>Processing Days (Max)</Label>
                    <Input
                      type="number"
                      value={formData.processing_days_max || ''}
                      onChange={(e) => setFormData(p => ({ ...p, processing_days_max: parseInt(e.target.value) || null }))}
                      placeholder="e.g., 7"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="documents" className="space-y-4">
                <div>
                  <Label className="mb-3 block">Select Required Documents</Label>
                  <p className="text-xs text-muted-foreground mb-4">
                    These documents will be automatically added to the checklist when an order is created for this service.
                  </p>
                  <div className="grid grid-cols-1 gap-2 max-h-[300px] overflow-y-auto">
                    {documentTypes.map((docType) => (
                      <div 
                        key={docType.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${formData.required_documents?.includes(docType.id) ? 'bg-teal-50 border-teal-200' : 'hover:bg-muted/50'}`}
                        onClick={() => handleToggleRequiredDoc(docType.id)}
                      >
                        <Checkbox 
                          checked={formData.required_documents?.includes(docType.id)}
                          onCheckedChange={() => handleToggleRequiredDoc(docType.id)}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{docType.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {docType.applicable_to || 'Both'} • {docType.requires_expiry !== false ? 'Has Expiry' : 'No Expiry'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    Selected: {formData.required_documents?.length || 0} documents
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="details" className="space-y-4">
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description of this service"
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Instructions</Label>
                  <Textarea
                    value={formData.instructions}
                    onChange={(e) => setFormData(p => ({ ...p, instructions: e.target.value }))}
                    placeholder="Special instructions for processing this service"
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Internal Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Internal notes (not shown to clients)"
                    rows={2}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData(p => ({ ...p, is_active: v }))}
                  />
                  <Label>Active Service</Label>
                </div>
              </TabsContent>
            </Tabs>
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-service-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Service'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
